import { createWordBoundaryRegex } from "@/utils/text-utils";

/**
 * Whether a record mentions any of a watchlist's tickers.
 *
 * Every feed has the same two kinds of place a ticker can appear, and they
 * need opposite treatment:
 *
 *   - An **exact** field the publisher filled in — `keyword_labels` on a
 *     headline, `symbol` on a government trade, `ticker` on an insider
 *     transaction. A hit here is authoritative at any length.
 *   - **Prose** — a subject, a company name, a member's name. Here a ticker is
 *     just a string of capitals, and so is ordinary English at one or two
 *     characters.
 *
 * Measured over 500 production headlines: bare `A` matched 30 times and was
 * wrong 30 times ("A senior Iranian official…"), `T` 15 of 15 ("T-note
 * futures…"), `PM` 23 of 23 — Philip Morris losing every time to "PM Lecornu",
 * "PM Takaichi", "Poland PM Tusk". At three characters the collision stops:
 * ALL, KEY, ONE and OUT scored 0 hits between them, because the desk does not
 * write those in capitals.
 *
 * Parentheses are house style for a symbol — "Apple (AAPL)", "Boeing (BA)" —
 * and were right every time in the same sample, which is what lets one and two
 * character tickers match prose at all.
 *
 * Kept apart from utils.ts so it can be tested without pulling in a component
 * tree — vitest runs with environment "node".
 */

/** Below this, a bare capitalised match in prose is noise rather than a ticker. */
export const MIN_BARE_TICKER_LEN = 3;

const escape = (term: string) => term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** Labels carry both `MSFT` and `MSFT.US`; compare on the part before the dot. */
const stem = (token: string) => token.split(".")[0].toUpperCase();

export interface TickerMatchFields {
  /**
   * Publisher-supplied identifiers, space separated or a single symbol.
   * Matched whole-token at any length.
   */
  exact?: string;
  /** Free text, where the length floor applies. */
  prose?: (string | undefined | null)[];
}

export function matchesTickerFields(
  fields: TickerMatchFields,
  tickers: string[]
): boolean {
  if (tickers.length === 0) return false;

  // 1. The exact field — authoritative, any length.
  if (fields.exact) {
    const labelled = new Set(
      fields.exact.split(/\s+/).filter(Boolean).map(stem)
    );
    if (labelled.size > 0 && tickers.some((t) => labelled.has(stem(t)))) {
      return true;
    }
  }

  const prose = (fields.prose ?? []).filter(Boolean).join(" ");
  if (!prose) return false;

  // 2. Parenthesised — house style, safe at any length.
  if (new RegExp(`\\((${tickers.map(escape).join("|")})\\)`).test(prose)) {
    return true;
  }

  // 3. Bare capitals, only where the string cannot also be a word.
  const bare = tickers.filter((t) => t.length >= MIN_BARE_TICKER_LEN);
  if (bare.length === 0) return false;
  return createWordBoundaryRegex(bare, false).test(prose);
}

export interface TickerMatchTarget {
  subject?: string;
  content?: string;
  source?: string;
  /** Curated labels, space separated. Authoritative when present. */
  keyword_labels?: string;
}

/** Headline-shaped adapter for {@link matchesTickerFields}. */
export function matchesAnyTicker(
  target: TickerMatchTarget,
  tickers: string[]
): boolean {
  return matchesTickerFields(
    {
      exact: target.keyword_labels,
      prose: [target.subject, target.content, target.source],
    },
    tickers
  );
}
